import React, { ChangeEvent, DragEvent, KeyboardEventHandler, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@televet/kibble-ui/build/components/Button';
import { Heading } from '@televet/kibble-ui/build/components/Heading';
import { Icon } from '@televet/kibble-ui/build/components/Icon';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { Link } from '@televet/kibble-ui/build/components/Link';
import { Alert } from '@televet/kibble-ui/build/components/Alert';
import {
  Modal,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
} from '@televet/kibble-ui/build/components/Modal';

import { Flex, UnorderedList, ListItem, Box, Center, HStack, VStack } from '@televet/kibble-ui/build/chakra';
import { formatPluralization } from 'shared/utils';
import { STATUSES, useCustomToast } from 'shared/hooks/useCustomToast';
import LoadingSpinner from 'shared/components/LoadingSpinner';
import useClinicUser from 'shared/hooks/useClinicUser';
import entries from 'lodash-es/entries';
import useZendesk from 'shared/hooks/useZendesk';
import { url as urlRegex } from 'shared/utils/validation';
import { useAuth } from 'shared/providers/AuthProvider';
import { Checkbox, TextInput } from '@televet/kibble-ui';

interface UploadFormsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const UploadFormsModal = ({ onClose, isOpen }: UploadFormsModalProps): JSX.Element => {
  const [showLinks, setShowLinks] = useState(false);
  const [formsToUpload, setFormsToUpload] = useState<Map<string, File | null>>(new Map());
  const [uploading, setUploading] = useState(false);
  const [link, setLink] = useState('');
  const [linkValidationMessage, setLinkValidationMessage] = useState('');
  const [fileSizeMessage, setFileSizeMessage] = useState('');
  const [noFormsMessage, setNoFormsMessage] = useState('');
  const { toastMessagePop } = useCustomToast();
  const hiddenFileInputRef = useRef<HTMLInputElement | null>(null);
  const [successfulForms, setSuccessfulForms] = useState<Map<string, string>>(new Map());
  const [failedForms, setFailedForms] = useState<Map<string, string>>(new Map());
  const { clinicUser, currentClinic } = useClinicUser();
  const { createFormBuildTicket } = useZendesk(clinicUser);
  const linkInputRef = useRef<HTMLInputElement | null>(null);

  const isLinkValid = useMemo(() => urlRegex.test(link), [link]);
  const handleLinkChange = (e: ChangeEvent<HTMLInputElement>): void => setLink(e.target.value);
  const handleLinkKeyDown: KeyboardEventHandler<HTMLInputElement> = (e): void => {
    if (e.key === 'Enter') {
      handleAddLink();
    }
  };

  useEffect(() => {
    if (!!linkValidationMessage && isLinkValid) {
      setLinkValidationMessage('');
    }
  }, [link, isLinkValid, linkValidationMessage]);

  const handleUpload = async (): Promise<void> => {
    setUploading(true);
    if (!formsToUpload.size) {
      setNoFormsMessage('Please add a form to upload.');
      setUploading(false);
      return;
    }

    try {
      // split forms list into files and links
      const links: string[] = [];
      const files: File[] = [];
      await Promise.all(
        entries(formsToUpload).map(async ([key, value]) => {
          if (value) {
            // value indicates its a file, not a URL
            // check for actual file content before attempting upload
            await value
              .arrayBuffer()
              .then(() => files.push(value))
              .catch(() => {
                setFailedForms((forms) => {
                  forms.set(key, 'File was renamed or moved');
                  return new Map(forms);
                });
              });
          } else {
            links.push(key);
          }
        }),
      );

      if (!clinicUser || !currentClinic?.id) return;

      const response = await createFormBuildTicket({
        clinicId: currentClinic.id,
        clinicName: currentClinic.name,
        userEmail: clinicUser.email || '',
        userName: [clinicUser.firstName, clinicUser.lastName].join(' ').trim(),
        files: files,
        sfAccountId: currentClinic.crmId || '',
        links: links,
        skipZendeskTicket: isImpersonating ? skipZendeskTicket : false,
        additionalNote: isImpersonating ? additionalNote : '',
      });

      let successMap = new Map();
      if (response?.successfulUploads && Object.keys(response.successfulUploads).length) {
        successMap = new Map(entries(response.successfulUploads));
      }
      if (links.length) successMap = new Map(entries(successMap).concat(links.map((url) => [url, ''])));
      setSuccessfulForms(new Map(successMap));

      if (response?.failedUploads && Object.keys(response.failedUploads).length) {
        setFailedForms(new Map(Object.entries(response.failedUploads)));
      }
    } catch (error) {
      console.error(error);
      toastMessagePop('Whoops! Something went wrong uploading your forms. Please try again.', STATUSES.error);
    } finally {
      setUploading(false);
    }
  };

  const handleHaveLink = (): void => {
    setShowLinks(true);
  };

  const handleAddLink = (): void => {
    if (!isLinkValid) {
      setLinkValidationMessage('Please enter a valid URL (e.g. https://example.com).');
      linkInputRef.current?.focus();
      return;
    }
    setFormsToUpload((forms) => {
      forms.set(link, null);
      return new Map(forms);
    });
    setLink('');
    setNoFormsMessage('');
    toastMessagePop('1 form added');
  };

  const handleClearAll = (): void => {
    setFormsToUpload(new Map());
    toastMessagePop('All forms cleared');
    setFileSizeMessage('');
  };

  const handleClearForm = (form: string): void => {
    setFormsToUpload((forms) => {
      forms.delete(form);
      return new Map(forms);
    });
    setFileSizeMessage('');
    toastMessagePop('Form cleared');
  };

  const handleFiles = (files: FileList): void => {
    setFileSizeMessage('');
    setNoFormsMessage('');
    let fileCount = 0;
    Array.from(files).forEach((file) => {
      if (file.size > 50 * (1024 * 1024)) {
        // 50 MB file size limit
        setFileSizeMessage('One or more files were too large to upload. Maximum file size is 50 MB.');
      } else {
        fileCount++;
        formsToUpload.set(file.name, file);
      }
    });

    toastMessagePop(fileCount + ' ' + formatPluralization({ word: 'form', singularCriteria: fileCount }) + ' added');
    setFormsToUpload(new Map(formsToUpload));
  };

  const handleFileInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const files = e?.target?.files;
    if (files) handleFiles(files);
  };

  const pluralize = useMemo(
    () => formatPluralization({ word: 'Form', singularCriteria: formsToUpload.size }),
    [formsToUpload.size],
  );

  const handleFileInputClick = (): void => {
    if (hiddenFileInputRef && hiddenFileInputRef.current) {
      hiddenFileInputRef.current.click();
    }
  };

  const onDrop = (e: DragEvent): void => {
    e.stopPropagation();
    e.preventDefault();
    const { files } = e.dataTransfer;
    handleFiles(files);
  };

  const onDragOver = (e: DragEvent): void => {
    e.stopPropagation();
    e.preventDefault();
  };

  const resetModal = (): void => {
    setFormsToUpload(new Map());
    setShowLinks(false);
    setLink('');
    setSuccessfulForms(new Map());
    setFailedForms(new Map());
    setNoFormsMessage('');
    setLinkValidationMessage('');
    setFileSizeMessage('');
  };

  const handleClose = (): void => {
    resetModal();
    onClose();
  };

  const { isImpersonating } = useAuth();
  const [skipZendeskTicket, setSkipZendeskTicket] = useState(false);
  const [additionalNote, setAdditionalNote] = useState('');
  return (
    <Modal onClose={handleClose} isOpen={isOpen} size="lg" isCentered scrollBehavior="inside">
      <ModalHeader size="lg" pb={1}>
        Upload Forms
      </ModalHeader>
      <ModalCloseButton />
      <ModalBody>
        {uploading ? (
          <Center my={8}>
            <LoadingSpinner message={'Uploading ' + pluralize.toLowerCase() + '...'} />
          </Center>
        ) : (
          <>
            {successfulForms.size || failedForms.size ? (
              <VStack w="100%">
                {successfulForms.size && (
                  <Alert
                    status="success"
                    borderRadius={4}
                    gap={3}
                    alignItems="flex-start"
                    description={
                      <Text fontWeight="bold">
                        {successfulForms.size}{' '}
                        {formatPluralization({ word: 'form', singularCriteria: successfulForms.size })} uploaded
                      </Text>
                    }
                  ></Alert>
                )}
                {failedForms.size && (
                  <Alert status="error" borderRadius={4} gap={3} alignItems="flex-start">
                    <VStack align="flex-start">
                      <HStack>
                        <Icon name="warningSign" variant="danger" />
                        <Text fontWeight="bold">
                          {failedForms.size} {formatPluralization({ word: 'form', singularCriteria: failedForms.size })}{' '}
                          could not be uploaded
                        </Text>
                      </HStack>
                      <UnorderedList pl={14}>
                        {entries(failedForms).map(([key, value], index) => (
                          <ListItem key={index}>{"'" + key + "' - " + (value ? value : 'Uknown error')}</ListItem>
                        ))}
                      </UnorderedList>
                    </VStack>
                  </Alert>
                )}
                <Box />
                <Text as="p">
                  <Text fontWeight="bold">Converting forms usually takes 24 hours</Text>, but may take longer depending
                  on the complexity and number of forms submitted.
                </Text>
                <Box h={2} />
                <Text>
                  Forms will appear here in Flow as they are converted. We will review the forms for quality and send an
                  email to <Link href={'mailto:' + clinicUser?.email}>{clinicUser?.email}</Link> when all forms have
                  been converted and reviewed.
                </Text>
              </VStack>
            ) : (
              <VStack w="100%" spacing={4} align="stretch">
                <Text as="p">
                  Provide files or links below and we will convert them to digital forms in Flow.{' '}
                  <Text fontWeight="bold">This usually takes 2-3 days</Text>, but may take longer depending on the
                  complexity and number of forms.
                </Text>
                <Center
                  p={3}
                  borderRadius={8}
                  borderStyle="dashed"
                  borderWidth={1}
                  transition="all normal ease-in-out"
                  _hover={{
                    bg: 'background.primaryAlpha.hover',
                    borderColor: 'border.focus',
                  }}
                  cursor="pointer"
                  onDrop={onDrop}
                  onDragOver={onDragOver}
                  onClick={handleFileInputClick}
                >
                  <VStack>
                    <Icon name="upload" size="xl" />
                    <Box textAlign="center">
                      <Heading size="sm">Click or drag and drop</Heading>
                      <Text size="sm" mt={0}>
                        Images, Word Docs, or PDFs
                      </Text>
                    </Box>
                  </VStack>
                </Center>
                <TextInput
                  onChange={handleFileInputChange}
                  onClick={(e): void => {
                    e.currentTarget.value = '';
                  }}
                  ref={hiddenFileInputRef}
                  display="none"
                  type="file"
                  multiple
                  accept="image/*, .doc, .docx, .pdf"
                />
                {showLinks ? (
                  <HStack justify="space-between">
                    <TextInput
                      value={link}
                      ref={linkInputRef}
                      autoFocus
                      isInvalid={!!linkValidationMessage}
                      onKeyDown={handleLinkKeyDown}
                      onChange={handleLinkChange}
                      placeholder="https://example.com"
                      errorText={linkValidationMessage}
                    />
                    <Button variant="secondary" onClick={handleAddLink}>
                      Add
                    </Button>
                  </HStack>
                ) : (
                  <Center>
                    <Button variant="ghost" iconName="chainLink" onClick={handleHaveLink}>
                      Have a link?
                    </Button>
                  </Center>
                )}
                {isImpersonating && (
                  <Box>
                    <Text mb={1} size="sm" fontWeight="bold">ZD Options</Text>
                    <Checkbox
                      id="skip-zendesk-ticket"
                      value="false"
                      isChecked={skipZendeskTicket}
                      size="sm"
                      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
                      onChange={(t): void => {
                        setSkipZendeskTicket(t.target.checked);
                      }}
                    >
                      Skip creating Zendesk ticket
                    </Checkbox>
                    <TextInput
                      value={additionalNote}
                      placeholder="Additional info for the zendesk ticket (optional)"
                      size="sm"
                      onChange={(e): void => setAdditionalNote(e.target.value)}
                      autoFocus
                      isDisabled={skipZendeskTicket}
                    />
                  </Box>
                )}

                {!!formsToUpload.size && (
                  <VStack spacing={1} w="100%" align="stretch">
                    <HStack justify="space-between" mb={1}>
                      <Text size="xs" variant="subtle">
                        {formsToUpload.size} {pluralize}
                      </Text>
                      <Button variant="ghostNeutral" size="xs" onClick={handleClearAll}>
                        Clear All
                      </Button>
                    </HStack>
                    <Flex
                      direction="column"
                      gap={1}
                      borderRadius={8}
                      borderWidth={1}
                      p={1}
                      maxH="164px"
                      overflowY="auto"
                    >
                      {entries(formsToUpload).map(([key], index) => (
                        <HStack
                          key={'form-to-upload-' + index}
                          justify="space-between"
                          w="100%"
                          p={1}
                          pl={2}
                          borderRadius={4}
                          bgColor="background.info"
                        >
                          <Text size="xs">{key}</Text>
                          <Button
                            iconName="trashcan"
                            size="xs"
                            variant="ghostNeutral"
                            onClick={(): void => handleClearForm(key)}
                          />
                        </HStack>
                      ))}
                    </Flex>
                  </VStack>
                )}
                {noFormsMessage && (
                  <Text mt={3} variant="danger">
                    {noFormsMessage}
                  </Text>
                )}
                {fileSizeMessage && (
                  <Text mt={3} variant="danger">
                    {fileSizeMessage}
                  </Text>
                )}
              </VStack>
            )}
          </>
        )}
      </ModalBody>
      <ModalFooter mb={2}>
        {!uploading && (successfulForms.size || failedForms.size) ? (
          <HStack>
            <Button variant="secondary" onClick={resetModal}>
              Upload More Forms
            </Button>
            <Button onClick={handleClose}>Got It</Button>
          </HStack>
        ) : (
          <HStack justify="space-between" w="100%">
            <Button variant="tertiary" onClick={handleClose} disabled={uploading}>
              Cancel
            </Button>
            <Button
              onClick={handleUpload}
              disabled={uploading}
              isLoading={uploading}
              loadingText="Uploading"
              spinnerPlacement="end"
            >
              Upload{formsToUpload.size ? ' ' + formsToUpload.size + ' ' + pluralize : null}
            </Button>
          </HStack>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default UploadFormsModal;
